import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CameraIcon, CheckIcon, FileTextIcon, ImageIcon, PlusIcon, UploadCloudIcon, XIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { useUploader } from '../../hooks/useUploader';
interface UploadPanelProps {
  onSubmit: (names: string[]) => void;
  submitLabel?: string;
  sampleName?: string;
}
export function UploadPanel({
  onSubmit,
  submitLabel = 'Submit document',
  sampleName = 'statement.pdf'
}: UploadPanelProps) {
  const {
    files,
    addFiles,
    addSample,
    remove,
    allDone
  } = useUploader();
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  return <div data-id="element-421" data-mp="169jypd4c47xo">
      <input ref={fileInput} type="file" multiple accept="image/*,application/pdf" className="sr-only" onChange={e => addFiles(e.target.files)} aria-label="Choose files" data-id="element-422" data-mp="169a75l3n5spv" />
      <input ref={cameraInput} type="file" accept="image/*" capture="environment" className="sr-only" onChange={e => addFiles(e.target.files)} aria-label="Take a photo" data-id="element-423" data-mp="126456pnkanp7" />

      {files.length === 0 ? <>
          {/* Mobile: camera-first */}
          <div className="grid gap-2.5 md:hidden" data-id="element-424" data-mp="3hs4n2yccxmu7">
            <Button size="lg" block icon={<CameraIcon className="h-5 w-5" data-id="element-426" data-mp="3lrwq6vizf0s6" />} onClick={() => cameraInput.current?.click()} data-id="element-425" data-mp="3lsomy9obf4k1">
              Take a photo
            </Button>
            <Button size="lg" variant="secondary" block icon={<UploadCloudIcon className="h-5 w-5" data-id="element-428" data-mp="ichgjveimt7k" />} onClick={() => fileInput.current?.click()} data-id="element-427" data-mp="ddn03040tgqm">
              Choose from files
            </Button>
            <button type="button" onClick={() => addSample(sampleName)} className="mt-1 text-center text-[13px] text-ink-400 underline-offset-2 hover:underline" data-id="element-429" data-mp="4kb4olieb8dx">
              Try with a sample file
            </button>
          </div>

          {/* Desktop: drag & drop */}
          <div onDragOver={e => {
        e.preventDefault();
        setDragging(true);
      }} onDragLeave={() => setDragging(false)} onDrop={e => {
        e.preventDefault();
        setDragging(false);
        addFiles(e.dataTransfer.files);
      }} className={`hidden flex-col items-center rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-[background-color,border-color] duration-150 md:flex ${dragging ? 'border-pulse-500 bg-pulse-50' : 'border-ink-200 bg-ink-50'}`} data-id="element-430" data-mp="11390ts01k40q">
            <motion.span animate={{
          y: dragging ? -4 : 0
        }} transition={{
          duration: 0.15
        }} className="grid h-12 w-12 place-items-center rounded-2xl bg-white text-pulse-600 shadow-soft" data-id="element-431" data-mp="n6qzn4d4cpv6">
              <UploadCloudIcon className="h-6 w-6" aria-hidden data-id="element-432" data-mp="ea06azpybp4a" />
            </motion.span>
            <p className="mt-4 font-medium text-ink-900" data-id="element-433" data-mp="98kln8rorkmf">{dragging ? 'Drop to upload' : 'Drag files here'}</p>
            <p className="mt-1 text-sm text-ink-500" data-id="element-434" data-mp="4ardia6togji">PDF, JPG or PNG · multiple pages welcome</p>
            <div className="mt-5 flex gap-2" data-id="element-435" data-mp="3qb6wz6u1o9su">
              <Button size="sm" variant="secondary" onClick={() => fileInput.current?.click()} data-id="element-436" data-mp="3va0b91h5o4u2">
                Browse files
              </Button>
              <Button size="sm" variant="ghost" onClick={() => addSample(sampleName)} data-id="element-437" data-mp="45084oiwthlx">
                Use sample file
              </Button>
            </div>
          </div>
        </> : <div data-id="element-438" data-mp="37migcx2lgqmv">
          <ul className="space-y-2" data-id="element-439" data-mp="37iul9thi3w61">
            <AnimatePresence initial={false} data-id="element-440" data-mp="3cjikv3jsplbv">
              {files.map((f, i) => {
            const done = f.progress >= 100;
            return <motion.li key={f.id} layout initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} className="flex items-center gap-3 rounded-xl bg-ink-50 p-3" data-id="element-441" data-mp="3hh20a6s8c45e" data-mpi={i}>
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white text-ink-500 ring-1 ring-ink-200" data-id="element-442" data-mp="ue95107lynca">
                      {f.kind === 'image' ? <ImageIcon className="h-4 w-4" data-id="element-443" data-mp="194q1041h4l1s" /> : <FileTextIcon className="h-4 w-4" data-id="element-444" data-mp="14cf3t8y4bdcx" />}
                    </span>
                    <div className="min-w-0 flex-1" data-id="element-445" data-mp="14ac4fyb2s7vj">
                      <div className="flex items-center justify-between gap-2" data-id="element-446" data-mp="1i2sbrbb0es29">
                        <p className="truncate text-sm font-medium text-ink-900" data-id="element-447" data-mp="1e2rmswqaaygy">
                          {files.length > 1 ? `Page ${i + 1} · ` : ''}
                          {f.name}
                        </p>
                        <span className="tabular shrink-0 text-xs text-ink-500" data-id="element-448" data-mp="1n3y5ncgeoldd">{done ? `${(f.size / 1e6).toFixed(1)} MB` : `${Math.round(f.progress)}%`}</span>
                      </div>
                      <div className="mt-2 h-1 overflow-hidden rounded-full bg-ink-200" data-id="element-449" data-mp="3out6wjtdtcrh">
                        <motion.div className={`h-full rounded-full ${done ? 'bg-positive-500' : 'bg-pulse-600'}`} animate={{
                    width: `${f.progress}%`
                  }} transition={{
                    duration: 0.12,
                    ease: 'linear'
                  }} data-id="element-450" data-mp="24bpad7ef9yyo" />
                      </div>
                    </div>
                    {done ? <motion.span initial={{
                scale: 0.6,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} className="grid h-6 w-6 place-items-center rounded-full bg-positive-500 text-white" data-id="element-451" data-mp="1zlilr7jwuegf">
                        <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} aria-label="Uploaded" data-id="element-452" data-mp="1qku9aha60m5n" />
                      </motion.span> : <button onClick={() => remove(f.id)} className="grid h-6 w-6 place-items-center rounded-full text-ink-400 hover:bg-ink-200" aria-label={`Remove ${f.name}`} data-id="element-453" data-mp="1bp4je81uzovn">
                        <XIcon className="h-3.5 w-3.5" data-id="element-454" data-mp="362nqa8hs4jtm" />
                      </button>}
                  </motion.li>;
          })}
            </AnimatePresence>
          </ul>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-455" data-mp="39kbgxzr2la0k">
            <Button size="sm" variant="secondary" icon={<PlusIcon className="h-4 w-4" data-id="element-457" data-mp="34jnln1vt670j" />} onClick={() => fileInput.current?.click()} data-id="element-456" data-mp="34koz1d0ar5lf">
              Add another page
            </Button>
            <Button size="sm" variant="ghost" icon={<CameraIcon className="h-4 w-4" data-id="element-459" data-mp="2vksixzrz3f1z" />} onClick={() => cameraInput.current?.click()} className="md:hidden" data-id="element-458" data-mp="2vjqumtly4w2b">
              Photo
            </Button>
            <Button size="sm" variant="ghost" onClick={() => addSample(`page-${files.length + 1}.pdf`)} data-id="element-460" data-mp="3sbuj64d9sk1j">
              Add sample page
            </Button>
          </div>
          <Button block size="lg" className="mt-6" disabled={!allDone} onClick={() => onSubmit(files.map(f => f.name))} data-id="element-461" data-mp="3iidm8rhu6otn">
            {allDone ? submitLabel : 'Uploading…'}
          </Button>
        </div>}
      <p className="mt-4 text-center text-xs text-ink-400" data-id="element-462" data-mp="ex7xckdt2nve">Encrypted end-to-end · Only your loan team can see your files</p>
    </div>;
}